import { useTranslation } from 'react-i18next'
import { Button, Spin, Table, Tooltip } from 'antd'
import { ClearOutlined, FilterOutlined } from '@ant-design/icons'

import { useCarriers, useContracts } from '@hooks'
import { Carrier, ContractFilters } from '@types'
import { useContractContracts, useGeneral } from '@contexts'

import { ContentContainer, StyledButton, StyledRow, StyledSelect } from '../StyledComponents'
import { useContractPagination } from './hooks/useContractsPagination'
import arrayToOptions from '../../utils/array-to-options'
import { initialContractsFiltersState } from '../../contexts/ContractsContext'
import FormatPrice from '../FormatPrice'

const ContractsDashboard = () => {
  const { filtersDisplayed, handleDisplayFilters, menuDisplayed } = useGeneral()
  const { allCarriersOptions } = useCarriers()
  const { filters, setFilters, isContractsSync, resetFilters } = useContractContracts()
  const { t } = useTranslation()
  const {
    contracts,
    contractsIsLoading,
    contractsFilters,
    contractsFiltersIsLoading,
    asnums,
    asnumsIsLoading,
  } = useContracts(filters)

  const pagination = useContractPagination({ filters, setFilters }, contracts?.total)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterOptions = (input: string, option: any) => {
    if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
    return true
  }

  const columns = [
    {
      title: t('contract_dashboard.contract'),
      dataIndex: 'contract',
      key: 'contract',
    },
    {
      title: t('contract_dashboard.position'),
      dataIndex: 'position',
      key: 'position',
    },
    {
      title: t('contract_dashboard.subPosition'),
      dataIndex: 'subPosition',
      key: 'subposition',
    },
    {
      title: t('contract_dashboard.description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('contract_dashboard.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: t('contract_dashboard.mu'),
      dataIndex: 'mu',
      key: 'mu',
    },
    {
      title: t('contract_dashboard.price'),
      dataIndex: 'price',
      key: 'price',
      render: (price: string) => <FormatPrice price={price} />,
    },
    {
      title: t('contract_dashboard.currency'),
      dataIndex: 'currency',
      key: 'currency',
    },
    {
      title: t('contract_dashboard.per'),
      dataIndex: 'per',
      key: 'per',
    },
    {
      title: t('contract_dashboard.service_number'),
      dataIndex: 'asnumId',
      key: 'asnumId',
    },
    {
      title: t('contract_dashboard.carrier'),
      key: 'businessName',
      dataIndex: 'carrier',
      render: (carrier: Carrier) => carrier && <>{carrier?.businessName}</>,
    },
    {
      title: t('contract_dashboard.observation'),
      dataIndex: 'observation',
      key: 'observation',
    },
  ]

  const handleContractFilterChange = (value: any) => {
    setFilters(({ asnum, ...prevState }: ContractFilters) => ({
      ...prevState,
      page: 1,
      contract: value as string,
    }))
  }

  const handleAsnumFilterChange = (value: any) => {
    if (value) {
      setFilters((prevState: ContractFilters) => ({
        ...prevState,
        page: 1,
        asnum: value as number,
      }))
    } else {
      handleClearFilters()
    }
  }

  const handleClearFilters = () => {
    setFilters(initialContractsFiltersState.filters)
  }

  const handleCarrierFilterChange = (value: any) => {
    setFilters({
      ...initialContractsFiltersState.filters,
      carrierId: value as number,
    })
  }

  return (
    <ContentContainer className="admin-dashboard">
      <StyledRow className="sticky-options border-botom">
        <div
          className={`filters-container${
            filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
          }`}
        >
          <Button onClick={handleDisplayFilters} id="displayFiltersButton">
            <FilterOutlined />
          </Button>
          <div className="filters-list">
            <Tooltip placement="bottomRight" title={t('globals.generics.clean_filters')}>
              <StyledButton
                className="inline option"
                onClick={resetFilters}
                icon={<ClearOutlined style={{ fontSize: '18px' }} />}
              />
            </Tooltip>
            <StyledSelect
              maxTagCount="responsive"
              allowClear
              placeholder={t('admin_router.filter_options.by_carrier.title')}
              options={allCarriersOptions}
              filterOption={handleFilterOptions}
              onChange={handleCarrierFilterChange}
              value={filters?.carrierId}
              onClear={handleClearFilters}
            />
            <StyledSelect
              maxTagCount="responsive"
              showSearch
              allowClear
              placeholder={t('admin_router.filter_options.contract_filters.contract')}
              options={contractsFilters?.map(arrayToOptions())}
              filterOption={handleFilterOptions}
              suffixIcon={null}
              value={filters?.contract}
              onChange={handleContractFilterChange}
              loading={contractsFiltersIsLoading}
            />
            <StyledSelect
              maxTagCount="responsive"
              allowClear
              placeholder={t('admin_router.filter_options.contract_filters.asnum')}
              showSearch
              options={asnums?.map(arrayToOptions())}
              onChange={handleAsnumFilterChange}
              value={filters?.asnum}
              loading={asnumsIsLoading}
              disabled={asnumsIsLoading}
            />
          </div>
        </div>
      </StyledRow>
      <Spin spinning={contractsIsLoading || isContractsSync}>
        <StyledRow className="data-table-container">
          <Table
            dataSource={contracts?.contracts}
            className="dataTable"
            columns={columns}
            pagination={pagination}
            showSorterTooltip={false}
            rowKey="id"
            scroll={{ x: true }}
            loading={contractsIsLoading}
          />
        </StyledRow>
      </Spin>
    </ContentContainer>
  )
}

export { ContractsDashboard }
